import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import {
  Badge,
  Button,
  Flex,
  Progress,
  Table,
  Typography,
  useMessagedAsync,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { bnplMerchantGetOrderList } from "@payping/redux/actions";
import { useSearchParams } from "react-router-dom";
import { dateFormats, moment, toJalali, toUtc } from "@payping/utils/dateUtils";
import { ComponentProps, useMemo, useState } from "react";
import { ColumnsType } from "@payping/web-components/components/Table/types";
import { OrderInfo, bnplMerchantApis } from "@payping/api";
import {
  MerchantOrderSteps,
  OrderStatusExcelRequest,
  OrderStatusRequest,
} from "@payping/api/app/api/bnplMerchantTypes";
import PageComponentForHandlingCrispInMobile from "@/components/mobile/HandlingCrispInMobile";
import { CuToman } from "@payping/icons";
import Filters from "./Filters";
import BnplListDetailsPage from "./Details";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";

const filters = {
  status: [
    "all",
    OrderStatusRequest.Defaulted,
    OrderStatusRequest.Delayed,
    OrderStatusRequest.SemiCanceled,
    OrderStatusRequest.Canceled,
    OrderStatusRequest.SalaryDeductionWaiting,
    OrderStatusRequest.Paying,
    OrderStatusRequest.Completed,
    OrderStatusRequest.ChequeDeliveryAcceptWaiting,
  ],
  searchVariant: ["consumerName", "trackingCode", "phoneNumber", "searchBy"],
} as const;

interface FiltersType {
  status: (typeof filters)["status"][number];
  startDate?: moment.Moment;
  endDate?: moment.Moment;
  searchVariant?: (typeof filters)["searchVariant"][number];
  searchValue: string;
  pageSize: number;
  pageNumber: number;
  creditAmount?: number;
}
const BnplListPage = () => {
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [detailsModal, setDetailsModal] = useState({
    trackingCode: "",
    isOpen: false,
  });

  const parsedFilters: FiltersType = useMemo(() => {
    const statusFromSearch = filters.status.find((i) => i.toString() === searchParams.get("status"));
    return {
      status: statusFromSearch || "all",
      startDate:
        typeof searchParams.get("startDate") === "string"
          ? toJalali({ date: searchParams.get("startDate") as string, dateFormat: dateFormats.standard.utcDateTime })
              .object || undefined
          : undefined,
      endDate:
        typeof searchParams.get("endDate") === "string"
          ? toJalali({ date: searchParams.get("endDate") as string, dateFormat: dateFormats.standard.utcDateTime })
              .object || undefined
          : undefined,

      searchVariant: filters.searchVariant.includes(searchParams.get("searchVariant") as any)
        ? (searchParams.get("searchVariant") as FiltersType["searchVariant"])
        : "searchBy",
      searchValue:
        typeof searchParams.get("searchValue") === "string" ? (searchParams.get("searchValue") as string) : "",

      pageNumber: !Number.isNaN(parseInt(searchParams.get("pageNumber") ?? "", 10))
        ? parseInt(searchParams.get("pageNumber") ?? "", 10)
        : 1,
      pageSize: !Number.isNaN(parseInt(searchParams.get("pageSize") ?? "", 10))
        ? parseInt(searchParams.get("pageSize") ?? "", 10)
        : 10,
      creditAmount: !Number.isNaN(parseInt(searchParams.get("creditAmount") ?? "", 10))
        ? parseInt(searchParams.get("creditAmount") ?? "", 10)
        : undefined,
    };
  }, [searchParams]);

  const searchValueParsed = !parsedFilters.searchValue.length ? undefined : parsedFilters.searchValue;

  const readyToUseFilters = useMemo(
    () =>
      ({
        startDate: parsedFilters.startDate
          ? toUtc({
              date: parsedFilters.startDate,
              toFormat: dateFormats.standard.utcDateTime,
            }).object?.toDate()
          : undefined,

        endDate: parsedFilters.endDate
          ? toUtc({
              date: parsedFilters.endDate,
              toFormat: dateFormats.standard.utcDateTime,
            }).object?.toDate()
          : undefined,
        consumerName: parsedFilters.searchVariant === "consumerName" ? searchValueParsed?.trim() : undefined,
        trackingCode: parsedFilters.searchVariant === "trackingCode" ? searchValueParsed?.trim() : undefined,
        phoneNumber: parsedFilters.searchVariant === "phoneNumber" ? searchValueParsed?.trim() : undefined,
        creditAmount: parsedFilters.creditAmount,
        status: parsedFilters.status === "all" ? undefined : parsedFilters.status,
        pageSize: parsedFilters.pageSize,
        pageNumber: parsedFilters.pageNumber,
      }) as const,
    [parsedFilters, searchValueParsed],
  );

  const orderListState = useMessagedAsync(
    async () => {
      const res = await dispatch(
        bnplMerchantGetOrderList(
          readyToUseFilters.startDate,
          readyToUseFilters.endDate,
          readyToUseFilters.consumerName,
          readyToUseFilters.phoneNumber,
          readyToUseFilters.creditAmount,
          readyToUseFilters.trackingCode,
          readyToUseFilters.status,
          readyToUseFilters.pageSize,
          readyToUseFilters.pageNumber,
        ),
      );
      return res;
    },
    [parsedFilters],
    { error: true, success: false },
  );

  const [downloadExcelStatus, downloadExcel] = useMessagedAsyncFn(
    async () => {
      const res = await bnplMerchantApis.orderListExcel(
        readyToUseFilters.startDate,
        readyToUseFilters.endDate,
        readyToUseFilters.consumerName,
        readyToUseFilters.phoneNumber,
        readyToUseFilters.creditAmount,
        readyToUseFilters.trackingCode,
        readyToUseFilters.status as unknown as OrderStatusExcelRequest,
      );

      return res;
    },
    [parsedFilters],
    { loading: false },
    { error: true, success: false },
  );

  const ordersList = useAppSelector((state) => state.bnplMerchant.orderList);

  const handleClearFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  const handleOpenDetailsModal = (trackingCode: string) => {
    setDetailsModal({
      isOpen: true,
      trackingCode,
    });
  };

  const handleCloseDetailsModal = () => {
    setDetailsModal({
      isOpen: false,
      trackingCode: "",
    });
  };

  const actions: ComponentProps<typeof Table>["actions"] = (rowIndex: number, record?: any) => [
    <Button
      type="link"
      onClick={() => {
        handleOpenDetailsModal(record.orderTrackingCode);
      }}
      key={rowIndex}
    >
      {t("common.details")}
    </Button>,
  ];

  const columns: ColumnsType<OrderInfo> = [
    { dataIndex: "tableIdCol", key: "tableIdCol", title: t("common.table.id"), width: "50px" },
    {
      dataIndex: "createdDate",
      ellipsis: true,
      key: "createdDate",
      title: t("common.date"),
      render(_, record) {
        if (!record.createdDate) return null;
        return <span>{moment(record.createdDate).format(dateFormats.ui.jalaliDate)}</span>;
      },
    },
    {
      dataIndex: "availableCreditAmount",
      ellipsis: true,
      key: "availableCreditAmount",
      title: t("shop.bnpl.creditAmount"),
      render(_, record) {
        if (!record.creditAmount) return null;
        return (
          <Flex align="center">
            <Typography.Text>{formatLocalizedNumber({ value: record.creditAmount })}</Typography.Text>
            <CuToman width={24} height={24} />
          </Flex>
        );
      },
    },
    {
      dataIndex: "consumer",
      ellipsis: true,
      key: "consumer",
      title: t("common.customer"),
    },

    {
      dataIndex: "paidoffInstallmentCount",
      ellipsis: true,
      key: "paidoffInstallmentCount",
      title: t("shop.bnpl.summeryStatus"),

      render(_, record) {
        if (record.orderStep === MerchantOrderSteps.Canceled) {
          return <Badge status="default" text={t("shop.bnpl.orderDetails.orderSteps.canceled")} />;
        }
        if (record.orderStep === MerchantOrderSteps.SemiCanceledRefundBalanceDeficit) {
          return (
            <Badge status="default" text={t("shop.bnpl.orderDetails.orderSteps.semiCanceledRefundBalanceDeficit")} />
          );
        }
        if (record.orderStep === MerchantOrderSteps.SemiCanceledRefundInProgress) {
          return <Badge status="default" text={t("shop.bnpl.orderDetails.orderSteps.semiCanceledRefundInProgress")} />;
        }
        if (record.orderStep === MerchantOrderSteps.SemiCanceledRefundInactive) {
          return <Badge status="default" text={t("shop.bnpl.orderDetails.orderSteps.semiCanceledRefundInactive")} />;
        }
        if (record.orderStep === MerchantOrderSteps.WaitingForChequeConfirm) {
          return <Badge status="warning" text={t("shop.bnpl.orderDetails.orderSteps.waitingForChequeConfirm")} />;
        }
        if (record.paidoffInstallmentCount === undefined || record.totalInstallmentCount === undefined) {
          return null;
        }
        return (
          <Progress
            showInfo
            type="line"
            percent={(record.paidoffInstallmentCount * 100) / record.totalInstallmentCount}
            format={() => `${record.totalInstallmentCount}/${record.paidoffInstallmentCount}`}
          />
        );
      },
    },
  ];

  const dataSource: ComponentProps<typeof Table>["dataSource"] = ordersList?.orders.map((item, index) => ({
    tableIdCol: parsedFilters.pageSize * (parsedFilters.pageNumber - 1) + index + 1,
    createdDate: item.createdDate,
    consumer: item.consumer || "",
    creditAmount: item.creditAmount,
    orderTrackingCode: item.orderTrackingCode,
    paidoffInstallmentCount: item.paidoffInstallmentCount,
    totalInstallmentCount: item.totalInstallmentCount,
    orderStep: item.orderStep,
    key: index,
  }));

  return (
    <>
      <PageComponentForHandlingCrispInMobile>
        <Flex gap={32} vertical className="tw-w-full">
          <Typography.Title level={4} className="!tw-m-0">
            {t("shop.bnpl.ordersList.title")}
          </Typography.Title>
          <Filters
            parsedFilters={parsedFilters}
            downloadExcel={downloadExcel}
            loadingDownloadExcel={downloadExcelStatus.loading}
            clearFilters={handleClearFilters}
          />
          <Table
            size="large"
            loading={orderListState.loading}
            columns={columns}
            actions={actions}
            dataSource={orderListState.error ? [] : dataSource}
            pagination={{
              current: parsedFilters.pageNumber,
              total: ordersList?.total,
              pageSize: parsedFilters.pageSize,
              pageSizeOptions: [10, 20, 50],
              onChange(page, pageSize) {
                setSearchParams((pre) => {
                  pre.set("pageNumber", page.toString());
                  return pre;
                });
                setSearchParams((pre) => {
                  pre.set("pageSize", pageSize.toString());
                  return pre;
                });
              },
            }}
          />
        </Flex>
      </PageComponentForHandlingCrispInMobile>
      {detailsModal.isOpen ? (
        <BnplListDetailsPage
          isOpen={detailsModal.isOpen}
          trackingCode={detailsModal.trackingCode}
          onClose={() => {
            handleCloseDetailsModal();
          }}
        />
      ) : null}
    </>
  );
};

export default BnplListPage;
